import { PromoBadgeContent } from "@silversea-ssc/ui-components-react";
import {
  INFINITE_STR_DATE,
  OptionalDate,
  noDate,
  optionalDate
} from "@silversea-ssc/dotcom-api-types/OptionalDate";
import { WebPromotion } from "@silversea-ssc/dotcom-api-types/CruisePriceAndOffersV2";
import { FareCode } from "@silversea-ssc/web-fares";
import { isMatching, match, P } from "ts-pattern";
import {
  CruiseOfferContentFragment,
  OfferContentFragment
} from "types/graphql-types";
import { dateFromOptionalDate } from "./date";
import { isPresent } from "./checks";
import { customLocationValuesFromSearch } from "./localization/geolocation/geolocationLoader";
import { OfferCard } from "components/OfferCard/type";
import { prefixAllTokens, RemotePromos } from "src/ssc-api/useRemotePromos";
import { forceResTimezone } from "./date/forceResTimezone";
import {
  TokenValueDate,
  TokenValueEncoded,
  TokenValues
} from "@silversea-ssc/dotcom-read-model-types/Tokens";

export type ActivePromoForCruise = {
  _key: string;
  code: string;
  activeForFares: readonly FareCode[];
  tokenValues: TokenValues;
  content: CruiseOfferContentFragment;
  toDate: OptionalDate;
  order: number;
};

export const activePromosForCruise = (
  promos: readonly WebPromotion[],
  promosContent: CruiseOfferContentFragment[]
): ActivePromoForCruise[] => {
  const allTokensPrefixed = prefixAllTokens(promos);

  return (promosContent || [])
    .reduce((acc, content) => {
      const promo = promos.find(
        byConfigurationCode(content.configuration?.code)
      );
      if (promo && content) {
        acc.push({
          _key: content._id || promo.configurationCode,
          code: promo.configurationCode,
          activeForFares: promo.activeFareCodes,
          tokenValues: { ...promo.tokenValues, ...allTokensPrefixed },
          toDate: expirationDateToken(promo.tokenValues),
          content,
          order: computeCurrentOrder(content.order)
        });
      }

      return acc;
    }, [] as ActivePromoForCruise[])
    .sort(sortByPromoOrder);
};

const expirationDateToken = (tokenValues: TokenValues): OptionalDate => {
  const maybeExpirationDate: TokenValueEncoded | undefined =
    tokenValues["expiration_date"];
  return maybeExpirationDate?.type === "date"
    ? optionalDate(maybeExpirationDate.date)
    : noDate();
};

export const activePromosPerFare = (
  activePromos: ActivePromoForCruise[],
  fare: FareCode
): ActivePromoForCruise[] => {
  return activePromos.filter((p) => p.activeForFares.includes(fare));
};

type PromoVisibleIn =
  | "cruiseFareTab"
  | "cruiseCard"
  | "cruisePageHero"
  | "urgencyBox"
  | "cruiseOfferSliderCard";
export const activePromosVisibleIn = (
  promos: ActivePromoForCruise[],
  visibleIn: PromoVisibleIn
): ActivePromoForCruise[] => {
  const filterFn = match(visibleIn)
    .with("cruiseCard", () => filterPromoForShow("showInCruiseCard"))
    .with("cruisePageHero", () => filterPromoForShow("showInCruisePageHero"))
    .with("cruiseFareTab", () => filterPromoForShow("showInCruiseFareTab"))
    .with("urgencyBox", () => filterPromoForShow("showUrgencyBox"))
    .with("cruiseOfferSliderCard", () =>
      filterPromoForHide("hideInCruisePageOfferSlider")
    )
    .exhaustive();

  return promos.filter(filterFn);
};

const filterPromoForShow = (key: string) => (promo: ActivePromoForCruise) =>
  !!promo.content[key];

const filterPromoForHide = (key: string) => (promo: ActivePromoForCruise) =>
  !promo.content[key];

type ValidatedPromoContent<T> = T & { code: string; title: string };
const validPromoContent = <
  T extends { code: string | undefined; title: string | undefined }
>(
  promo
): promo is ValidatedPromoContent<T> =>
  isMatching({ code: P.string, title: P.string })(promo);

export const filterValidPromosContent = <T>(promos: T[]) =>
  promos.filter((x): x is ValidatedPromoContent<T> => validPromoContent(x));

export const activePromosToPromoBadges = (
  activePromos: ActivePromoForCruise[]
): PromoBadgeContent[] =>
  filterValidPromosContent(
    activePromos.map((p) => ({
      code: p.code,
      title: p.content.title?.localized
    }))
  );

const computeCurrentOrder = (order: OfferContentFragment["order"]): number =>
  order?.[0]?.content || 0;

export const isPromoActiveTodayWithOverride = (
  date: OptionalDate | undefined,
  querySearch: string | undefined
) => {
  const customValues = querySearch
    ? customLocationValuesFromSearch(querySearch)
    : {};
  const overriddenDate = customValues.date;
  const today = overriddenDate ? new Date(overriddenDate) : new Date();

  return isPromoActiveToday(date, today);
};

export const isPromoActiveToday = (
  expirationDate: OptionalDate | undefined,
  now: Date
) => {
  if (!expirationDate) return false;

  const promoExpirationDate = dateFromOptionalDate(expirationDate);
  if (!promoExpirationDate) return true;

  return forceResTimezone(promoExpirationDate) >= now;
};

const baseExpirationDateToken = (
  tokenValues: TokenValues
): TokenValueDate | undefined => {
  const maybeExpirationDate: TokenValueEncoded | undefined =
    tokenValues["expiration_date"];
  return maybeExpirationDate?.type === "date" ? maybeExpirationDate : undefined;
};

const expirationDateOrDefault = (
  tokenValues: TokenValues,
  _default: string
): string => baseExpirationDateToken(tokenValues)?.date || _default;

export const expirationDateOrInfinite = (tokenValues: TokenValues): string =>
  expirationDateOrDefault(tokenValues, INFINITE_STR_DATE);

const sortByPromoOrder = <T extends { order: number | undefined }>(
  a: T,
  b: T
): number => (a?.order || 0) - (b?.order || 0);

export const mergeOffersStaticContentWithRemote = <
  T extends OfferContentFragment,
  C extends Omit<RemotePromos, "cardContent">
>(
  data: T[],
  remoteData: C | undefined
): OfferCard[] =>
  (data || [])
    .filter(isPresent)
    .map<OfferCard | undefined>((content) => {
      const configurationCode = content.configuration?.code;
      if (!isPresent(configurationCode)) return undefined;

      const dynamicData = (remoteData || []).find(
        byConfigurationCode(configurationCode)
      );

      if (!isPresent(dynamicData)) return undefined;

      return {
        ...dynamicData,
        _id: content._id!,
        trackName: content.metadata?.tracking?.name,
        configurationCode: configurationCode,
        fullPath: content.fullPath,
        order: computeCurrentOrder(content.order),
        cardContent: {
          image: content?.card?.image,
          title: content?.card?.blockTitle?.localized,
          description: content?.card?.blockDescription?.localized
        }
      } satisfies OfferCard;
    })
    .filter(isPresent)
    .sort(sortByPromoOrder);

export const byConfigurationCode =
  (code: string | undefined) =>
  <
    T extends
      | { configurationCode?: string }
      | { configuration?: { code?: string } }
  >(
    promo: T | undefined | Partial<T> | undefined
  ): promo is T extends { configurationCode: string }
    ? T & { configurationCode: string }
    : T extends { configuration?: { code?: string } }
      ? T & { configuration: T["configuration"] & { code: string } }
      : T => {
    if (!promo) return false;

    if ("configurationCode" in promo) {
      return promo.configurationCode === code;
    }
    if ("configuration" in promo) {
      return promo.configuration?.code === code;
    }

    return false;
  };

export const byConfigurationAlgoliaCode =
  (code: number | undefined) =>
  <
    T extends
      | { algoliaCode?: number }
      | { configuration?: { algoliaCode?: number } }
  >(
    promo: T | undefined | Partial<T> | undefined
  ): promo is T extends { algoliaCode: number }
    ? T & { algoliaCode: number }
    : T extends { configuration?: { algoliaCode?: number } }
      ? T & { configuration: T["configuration"] & { algoliaCode: number } }
      : T => {
    if (!promo) return false;

    if ("algoliaCode" in promo) {
      return promo.algoliaCode === code;
    }
    if ("configuration" in promo) {
      return promo.configuration?.algoliaCode === code;
    }

    return false;
  };
